<template>
  <div class="leader-game-assign">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="page-header">
        <div class="page-title">
          <i class="el-icon-connection"></i>
          <span>创作者游戏分配</span>
        </div>
        <div class="page-desc">为每位创作者配置 App 端可见的游戏列表；已被分配的游戏对其他创作者不可见</div>
      </div>

      <div class="assign-layout">
        <!-- 左侧：创作者列表 -->
        <div class="leader-panel">
          <div class="panel-toolbar">
            <el-input
              v-model="leaderSearch"
              placeholder="搜索手机号 / ID"
              prefix-icon="el-icon-search"
              clearable
              size="small"
            />
            <el-button size="small" icon="el-icon-refresh" :loading="leadersLoading" @click="fetchLeaders">刷新</el-button>
          </div>

          <div v-loading="leadersLoading" class="leader-list">
            <el-empty v-if="!leadersLoading && filteredLeaders.length === 0" description="暂无创作者" :image-size="72" />
            <div
              v-for="leader in filteredLeaders"
              :key="leader.id"
              class="leader-item"
              :class="{ active: selectedLeader && selectedLeader.id === leader.id }"
              @click="selectLeader(leader)"
            >
              <el-avatar :size="40" :src="leader.avatarUrl" icon="el-icon-user-solid" />
              <div class="leader-info">
                <div class="leader-name">{{ leader.phone || ('创作者' + leader.id) }}</div>
                <div class="leader-meta">
                  <span>ID {{ leader.id }}</span>
                  <span class="dot">·</span>
                  <span>已分配 {{ leader.gameCount != null ? leader.gameCount : '-' }} 款</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：游戏分配 -->
        <div class="game-panel">
          <template v-if="selectedLeader">
            <div class="panel-header">
              <div class="selected-leader">
                <span class="label">当前创作者</span>
                <span class="value">{{ selectedLeader.phone || ('创作者' + selectedLeader.id) }}</span>
                <el-tag size="mini" type="info">ID {{ selectedLeader.id }}</el-tag>
              </div>
              <div class="header-actions">
                <el-button size="small" @click="selectAllOnShelf">全选上架</el-button>
                <el-button size="small" @click="clearSelection">清空</el-button>
                <el-button type="primary" size="small" :loading="saving" icon="el-icon-check" @click="saveAssign">
                  保存分配
                </el-button>
              </div>
            </div>

            <div class="game-toolbar">
              <el-input
                v-model="gameSearch"
                placeholder="搜索游戏名称"
                prefix-icon="el-icon-search"
                clearable
                size="small"
                class="game-search"
              />
              <span class="game-summary">
                已选 <strong>{{ selectedGameIds.length }}</strong> 款
                <span class="text-muted">/ 共 {{ allGames.length }} 款</span>
              </span>
            </div>

            <div v-loading="gamesLoading" class="game-grid">
              <el-empty v-if="!gamesLoading && filteredGames.length === 0" description="暂无游戏，请先在游戏管理中添加" />
              <div
                v-for="game in filteredGames"
                :key="game.id"
                class="game-card"
                :class="{
                  selected: selectedGameIds.includes(game.id),
                  offline: game.status !== 1
                }"
                @click="toggleGame(game.id)"
              >
                <div class="game-check">
                  <i v-if="selectedGameIds.includes(game.id)" class="el-icon-check"></i>
                </div>
                <div class="game-icon-wrap">
                  <el-image v-if="game.icon" :src="game.icon" fit="contain" class="game-icon" />
                  <div v-else class="game-icon-placeholder"><i class="el-icon-video-play"></i></div>
                </div>
                <div class="game-name" :title="game.name">{{ game.name }}</div>
                <el-tag :type="game.status === 1 ? 'success' : 'info'" size="mini" effect="plain">
                  {{ game.status === 1 ? '上架' : '下架' }}
                </el-tag>
              </div>
            </div>
          </template>

          <div v-else class="game-empty">
            <el-empty description="请从左侧选择一位创作者进行游戏分配">
              <el-button type="primary" plain size="small" @click="$router.push('/game')">前往游戏管理</el-button>
            </el-empty>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { get, post } from '@/utils/http'

export default {
  name: 'LeaderGameAssign',
  data() {
    return {
      leaders: [],
      allGames: [],
      leadersLoading: false,
      gamesLoading: false,
      saving: false,
      leaderSearch: '',
      gameSearch: '',
      selectedLeader: null,
      selectedGameIds: []
    }
  },
  computed: {
    filteredLeaders() {
      const kw = (this.leaderSearch || '').trim().toLowerCase()
      if (!kw) return this.leaders
      return this.leaders.filter(l => {
        const phone = (l.phone || '').toLowerCase()
        return phone.includes(kw) || String(l.id).includes(kw)
      })
    },
    filteredGames() {
      const kw = (this.gameSearch || '').trim().toLowerCase()
      if (!kw) return this.allGames
      return this.allGames.filter(g => (g.name || '').toLowerCase().includes(kw))
    }
  },
  mounted() {
    this.fetchLeaders()
  },
  methods: {
    async fetchLeaders() {
      this.leadersLoading = true
      try {
        const result = await get('/admin/users', { role: 1, page: 1, pageSize: 500 })
        const data = result.data || {}
        this.leaders = Array.isArray(data.users) ? data.users : []
        await this.loadLeaderGameCounts()
        if (this.selectedLeader) {
          const refreshed = this.leaders.find(l => l.id === this.selectedLeader.id)
          if (refreshed) this.selectedLeader = refreshed
        }
      } catch (e) {
        this.$message.error(e.message || '加载创作者列表失败')
        this.leaders = []
      } finally {
        this.leadersLoading = false
      }
    },
    async loadLeaderGameCounts() {
      await Promise.all(this.leaders.map(async leader => {
        try {
          const result = await get(`/admin/game/leader/${leader.id}`)
          const ids = Array.isArray(result.data) ? result.data : []
          this.$set(leader, 'gameCount', ids.length)
        } catch {
          this.$set(leader, 'gameCount', 0)
        }
      }))
    },
    async fetchAssignableGames(leaderId) {
      this.gamesLoading = true
      try {
        const result = await get('/admin/game/assignable', { leaderId })
        this.allGames = Array.isArray(result.data) ? result.data : []
      } catch (e) {
        this.$message.error(e.message || '加载可分配游戏失败')
        this.allGames = []
      } finally {
        this.gamesLoading = false
      }
    },
    async selectLeader(leader) {
      if (this.selectedLeader && this.selectedLeader.id === leader.id) return
      this.selectedLeader = leader
      this.selectedGameIds = []
      this.gamesLoading = true
      try {
        const [assignableResult, assignedResult] = await Promise.all([
          get('/admin/game/assignable', { leaderId: leader.id }),
          get(`/admin/game/leader/${leader.id}`)
        ])
        this.allGames = Array.isArray(assignableResult.data) ? assignableResult.data : []
        this.selectedGameIds = Array.isArray(assignedResult.data) ? [...assignedResult.data] : []
      } catch (e) {
        this.$message.error(e.message || '加载已分配游戏失败')
        this.allGames = []
        this.selectedGameIds = []
      } finally {
        this.gamesLoading = false
      }
    },
    toggleGame(gameId) {
      const idx = this.selectedGameIds.indexOf(gameId)
      if (idx >= 0) {
        this.selectedGameIds.splice(idx, 1)
      } else {
        this.selectedGameIds.push(gameId)
      }
    },
    selectAllOnShelf() {
      const onShelfIds = this.allGames.filter(g => g.status === 1).map(g => g.id)
      const merged = new Set([...this.selectedGameIds, ...onShelfIds])
      this.selectedGameIds = Array.from(merged)
    },
    clearSelection() {
      this.selectedGameIds = []
    },
    async saveAssign() {
      if (!this.selectedLeader) return
      this.saving = true
      try {
        await post('/admin/game/assign', {
          leaderId: this.selectedLeader.id,
          gameIds: this.selectedGameIds
        })
        this.$message.success('分配保存成功')
        this.$set(this.selectedLeader, 'gameCount', this.selectedGameIds.length)
        const idx = this.leaders.findIndex(l => l.id === this.selectedLeader.id)
        if (idx >= 0) {
          this.$set(this.leaders[idx], 'gameCount', this.selectedGameIds.length)
        }
        await this.fetchAssignableGames(this.selectedLeader.id)
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 20px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.page-title i {
  color: #e6a23c;
  font-size: 20px;
}

.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}

.assign-layout {
  display: flex;
  gap: 16px;
  min-height: 560px;
}

.leader-panel {
  width: 300px;
  flex-shrink: 0;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-toolbar {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

.panel-toolbar .el-input {
  flex: 1;
}

.leader-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  min-height: 480px;
}

.leader-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.leader-item:hover {
  border-color: #dcdfe6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.leader-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.leader-info {
  flex: 1;
  min-width: 0;
}

.leader-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leader-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.leader-meta .dot {
  margin: 0 4px;
}

.game-panel {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #ebeef5;
  background: linear-gradient(90deg, #fafafa 0%, #fff 100%);
}

.selected-leader {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-leader .label {
  font-size: 13px;
  color: #909399;
}

.selected-leader .value {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.game-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.game-search {
  max-width: 240px;
}

.game-summary {
  font-size: 13px;
  color: #606266;
}

.game-summary strong {
  color: #409eff;
  font-size: 16px;
}

.text-muted {
  color: #c0c4cc;
}

.game-grid {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  align-content: start;
  min-height: 420px;
}

.game-card {
  position: relative;
  padding: 12px 10px 10px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.game-card:hover {
  border-color: #c6e2ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
}

.game-card.selected {
  border-color: #409eff;
  background: #ecf5ff;
}

.game-card.offline {
  opacity: 0.72;
}

.game-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #dcdfe6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
}

.game-card.selected .game-check {
  background: #409eff;
  border-color: #409eff;
}

.game-icon-wrap {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.game-icon {
  width: 48px;
  height: 48px;
}

.game-icon-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 22px;
}

.game-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.game-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
}

@media (max-width: 992px) {
  .assign-layout {
    flex-direction: column;
  }

  .leader-panel {
    width: 100%;
    max-height: 280px;
  }

  .leader-list {
    min-height: 200px;
  }
}
</style>
